import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Spinner, Toast } from '../../lib';
import apiServices from '../../services/apiServices';
import {
  AcknowledgePortalDocument,
  GetMemberDocument,
  MemberDocumentAcknowledgment,
  MemberDocumentManifest,
} from '../../services/dtos';
import { docsApiError } from './DocumentsPage';

// D3 watermarked viewer (§5.9): a lazy-loading page scroller over the
// authenticated image endpoint. Every image the server sends is burned with
// the viewer's own name/member#/timestamp — that watermark IS the control, so
// there are no download/print affordances, just pages. Manifest dims reserve
// each page's aspect ratio so lazy loading never jumps the scroll.

// F1's two renditions: 720w for phones, 1440w otherwise, picked by the
// device's effective pixel width once per mount.
function pickRendition(): number {
  const effective = window.innerWidth * (window.devicePixelRatio || 1);
  return effective <= 800 ? 720 : 1440;
}

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  return isNaN(d.getTime())
    ? ''
    : d.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
}

const DocumentViewerPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const here = `/docs/${id}`;

  const [manifest, setManifest] = useState<MemberDocumentManifest | null>(null);
  const [blockedMessage, setBlockedMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const rendition = useRef(pickRendition());
  // one access re-check per failure burst, not one per broken <img>
  const rechecking = useRef(false);

  // D4 acknowledgment (§5.11): L1 button / L1.5 typed name, one-way
  const [typedName, setTypedName] = useState('');
  const [ackBusy, setAckBusy] = useState(false);
  const [ackError, setAckError] = useState<string | null>(null);

  const requiresAck =
    manifest?.reviewMode === 'Acknowledge' || manifest?.reviewMode === 'TypedName';
  const acked = !!manifest?.acknowledgedAtUtc;

  const load = () =>
    apiServices.client
      .get(new GetMemberDocument({ id: Number(id) }))
      .then((m: MemberDocumentManifest) => {
        setManifest(m);
        setError(null);
        setReloadKey((k) => k + 1);
      })
      .catch((e: any) => {
        const r = docsApiError(e, navigate, here);
        if (r?.blockedMessage) setBlockedMessage(r.blockedMessage);
        else if (r?.error) setError(r.error);
      });

  useEffect(() => {
    load();
  }, [id]);

  // A failing image usually means the ~30-min elevation lapsed mid-read: ask
  // the server, and either bounce to /verify or offer a retry.
  const onImageError = () => {
    if (rechecking.current) return;
    rechecking.current = true;
    apiServices.client
      .get(new GetMemberDocument({ id: Number(id) }))
      .then(() => setError('Some pages failed to load — use Reload to try again.'))
      .catch((e: any) => {
        const r = docsApiError(e, navigate, here);
        if (r?.blockedMessage) setBlockedMessage(r.blockedMessage);
        else if (r?.error) setError(r.error);
      })
      .then(() => {
        rechecking.current = false;
      });
  };

  const acknowledge = () => {
    if (ackBusy || !manifest) return;
    setAckBusy(true);
    setAckError(null);
    apiServices.client
      .post(
        new AcknowledgePortalDocument({
          id: Number(id),
          typedName: manifest.reviewMode === 'TypedName' ? typedName : undefined,
        })
      )
      .then((a: MemberDocumentAcknowledgment) => {
        setManifest({
          ...manifest,
          acknowledgedAtUtc: a.acknowledgedAtUtc,
          acknowledgedTypedName: a.typedName,
        });
      })
      .catch((e: any) => {
        const code = e?.responseStatus?.errorCode;
        if (code === 'StepUpRequired' || code === 'NotGoodStanding') {
          const r = docsApiError(e, navigate, here);
          if (r?.blockedMessage) setBlockedMessage(r.blockedMessage);
        } else {
          setAckError(e?.responseStatus?.message || 'Acknowledging failed — try again.');
        }
      })
      .then(() => setAckBusy(false));
  };

  const pageUrl = (n: number) =>
    `${apiServices.apiUrl()}/documents/${id}/page/${n}?R=${rendition.current}`;

  return (
    <div className="mx-auto w-full max-w-4xl flex flex-col gap-4 dark:text-white">
      <div>
        <Link
          to="/docs"
          className="text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-400"
        >
          ← All documents
        </Link>
        {manifest && (
          <div className="mt-1 flex flex-wrap items-baseline gap-x-3">
            <h2 className="text-xl font-bold break-words">{manifest.title}</h2>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {manifest.pageCount} page{manifest.pageCount === 1 ? '' : 's'}
            </span>
          </div>
        )}
      </div>

      {error && (
        <Toast color="failure">
          <div className="flex items-center gap-3">
            <span>{error}</span>
            <button onClick={load} className="font-medium underline">
              Reload
            </button>
          </div>
        </Toast>
      )}

      {blockedMessage && (
        <div className="rounded-lg border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900 p-4 text-sm text-yellow-800 dark:text-yellow-200">
          {blockedMessage}
        </div>
      )}

      {!manifest && !error && !blockedMessage && <Spinner color="info" />}

      {manifest && requiresAck && acked && (
        <div className="rounded-lg border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900 p-4 text-sm text-green-800 dark:text-green-200 print:hidden">
          You acknowledged this document on {fmtWhen(manifest.acknowledgedAtUtc!)}
          {manifest.acknowledgedTypedName && (
            <> — signed as &ldquo;{manifest.acknowledgedTypedName}&rdquo;</>
          )}
          .
        </div>
      )}

      {manifest && requiresAck && !acked && (
        <div className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900 p-4 text-sm text-amber-800 dark:text-amber-200 print:hidden">
          This document requires your acknowledgment — read it through, then use
          the button at the end.
          {manifest.dueAtUtc && (
            <>
              {' '}
              Please acknowledge by{' '}
              {new Date(manifest.dueAtUtc).toLocaleDateString([], {
                dateStyle: 'medium',
                timeZone: 'UTC',
              })}
              .
            </>
          )}
        </div>
      )}

      {/* Policy signal, not security: Ctrl+P prints this notice instead of
          pages. The real control is the per-request watermark — anything
          captured off the screen still carries the viewer's identity. */}
      <div className="hidden print:block text-sm">
        Printing is disabled — union documents are watermarked and for
        personal viewing in the member portal.
      </div>

      {manifest && (
        <div className="flex flex-col gap-3 print:hidden">
          {manifest.pages.map((p) => (
            <img
              key={`${p.pageNumber}-${reloadKey}`}
              src={pageUrl(p.pageNumber)}
              alt={`Page ${p.pageNumber} of ${manifest.title}`}
              loading="lazy"
              onError={onImageError}
              style={{ aspectRatio: `${p.width} / ${p.height}` }}
              className="w-full rounded border border-gray-200 dark:border-gray-700 bg-white"
            />
          ))}
        </div>
      )}

      {manifest && requiresAck && !acked && (
        <div className="flex flex-col gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4 print:hidden">
          {/* §5.11 evidence binding: the assent wording is SERVER-owned and
              rendered verbatim — the ack stores this exact versioned string,
              so what the record says was shown IS what was shown. */}
          <div className="text-sm font-medium">
            {manifest.assentText ||
              (manifest.reviewMode === 'TypedName'
                ? 'Type your full name to acknowledge you have read this document.'
                : 'Please confirm you have read this document.')}
          </div>
          {manifest.reviewMode === 'TypedName' && (
            <input
              id="ack-typed-name"
              name="typedName"
              type="text"
              value={typedName}
              onChange={(e) => setTypedName(e.target.value)}
              placeholder="Your full name"
              className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-2 text-sm"
            />
          )}
          {ackError && (
            <div className="text-sm text-red-600 dark:text-red-400">{ackError}</div>
          )}
          <button
            onClick={acknowledge}
            disabled={
              ackBusy ||
              (manifest.reviewMode === 'TypedName' && typedName.trim() === '')
            }
            className="self-start rounded bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-500 disabled:opacity-50"
          >
            I acknowledge this document
          </button>
          <div className="text-xs text-gray-500 dark:text-gray-400">
            Your acknowledgment is recorded once, with your name and the current
            time, and can&apos;t be undone.
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentViewerPage;
